import { z } from 'zod'
import libphonenumber from "google-libphonenumber";


const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
const VALID_DOCTOR_AGE = 18;

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional().default(false),
})

export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  role: z.string().min(1, 'Role is required'),
})

// User management form schema
export const createUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)'),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export const updateUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)')
    .optional()
    .or(z.literal('')),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2), // 0 = Inactive, 1 = Active, 2 = Suspended
})
export const addressSchema = z.object({
  address1: z.string().min(1, 'This field is required'),
  address2: z.string().optional().nullable(),
  country: z.string().min(1, 'Country is required'),
  state: z.string().min(1, 'State is required'),
  city: z.string().min(1, 'City is required'),
  pincode: z.string().min(1, 'Pincode is required'),
});

const optionalAddressSchema = z.object({
  address1: z.string().optional().nullable(),
  address2: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  pincode: z.string().optional().nullable(),
}).optional().nullable().transform((val, ctx) => {
  if (!val) return val;
  
  const hasAnyValue = Object.values(val).some(v => 
    v !== null && v !== undefined && v !== ''
  );
  
  if (hasAnyValue) {
    if (!val.address1 || val.address1.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Address line 1 is required',
        path: ['address1'],
      });
        }
    if (!val.country || val.country === "Select") {
      ctx.addIssue({
        code: 'custom',
        message: 'Country is required',
        path: ['country'],
      });
    }
    if (!val.state || val.state === "Select") {
      ctx.addIssue({
        code: 'custom',
        message: 'State is required',
        path: ['state'],
      });
    }
    if (!val.city || val.city === "Select") {
      ctx.addIssue({
        code: 'custom',
        message: 'City is required',
        path: ['city'],
      });
    }
    if (!val.pincode || val.pincode.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Pincode is required',
        path: ['pincode'],
      });
    }
  }
  
  return val;
});

export const availabilitySchema = z.object({
  session_type: z.string().min(1),
  session_day: z.string().min(1, 'Availability day is required'),
  start_time: z.string().min(1, 'Availability start time is required'),
  end_time: z.string().min(1, 'Availability end time is required'),
});

export const imageSchema = z.object({
  file_name: z.string(),
  file_type: z.string(),
  file_size: z.number().int(),
  width: z.number().int().optional().nullable(),
  height: z.number().int().optional().nullable(),
  file_dimensions: z.string().optional().nullable(),
  file_url: z.string().url(),
  thumbnail_url: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  short_descriptions: z.string().optional().nullable(),
});

const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);

export const createDoctorUserSchema = z.object({
  user_login: z.string().min(3),
  user_fullname: z.string().min(3),
  user_email: z.string().email(),
  user_phone_number: z.string().regex(phoneRegex, 'Invalid phone number.'),

  birthday: z.string()
  .refine((val) => !isNaN(Date.parse(val)), 'Invalid date')
  .refine((val) => {
    const birthDate = new Date(val);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      return age - 1 >= 18;
    }
    return age >= 18;
  }, 'You must be at least 18 years old'),

  gender: z.enum(['male', 'female', 'other'], {
    message: "This field is required.",
  }),
  bio: z.string().optional().nullable(),

  branch_id: z.string().refine((val) => val !== "Select", {
    message: "This field is required.",
  }),
  department_id: z.string().refine((val) => val !== "Select", {
    message: "This field is required.",
  }),
  designation_id: z.string().refine((val) => val !== "Select", {
    message: "This field is required.",
  }),
  language_id: z.array(z.string()).optional(),

  years_of_experience: z.number({message: "This field is required."}).int().min(0),
  blood_group: z.enum( ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], { message: "This field is required." }),
  medical_license_number: z.string().min(3),

  appointment_type: z.string().optional().nullable(),
  advance_booking_days: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined
        ? undefined
        : Number(val),
    z.number().int().min(0)
  ).optional(),
  appointment_slot_duration: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined
        ? undefined
        : Number(val),
    z.number().int().min(0)
  ).optional(),
  appointment_duration: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined
        ? undefined
        : Number(val),
    z.number().int().min(0)
  ).optional(),
  consultation_fee: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined
        ? undefined
        : Number(val),
    z.number().int().min(0)
  ).optional(),
  address: optionalAddressSchema,

  availability: z.array(availabilitySchema).optional().nullable(),

  image: imageSchema.optional().nullable(),
});

export const createPatientUserSchema = z.object({
  user_firstname: z.string().min(3),
  user_lastname: z.string().min(3),
  user_email: z.string().email(),
  user_phone_number: z.string().regex(phoneRegex, 'Invalid phone number.'),

  birthday: z.string()
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date')
    .refine((val) => {
      const birthDate = new Date(val);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        return age - 1 >= 0;
      }
      return age >= 0;
    }, 'Invalid birth date'),

  gender: z.enum(['male', 'female', 'other'], {
    message: "This field is required.",
  }),

  blood_group: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], { 
    message: "This field is required." 
  }),

  primary_doctor_id: z.string().nullable().optional(),

  user_status: z.string().min(1, "This field is required."),

  address: optionalAddressSchema,

  profile_picture: imageSchema.optional().nullable(),
});

export type CreateUserFormData = z.infer<typeof createUserSchema> 
export type UpdateUserFormData = z.infer<typeof updateUserSchema> 
export type CreateDoctorUserFormData = z.infer<typeof createDoctorUserSchema>
export type CreatePatientUserFormData = z.infer<typeof createPatientUserSchema>

export const categorySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
  parent_id: z.number().optional(),
})

// Category management form schema
export const createCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export const updateCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export type CreateCategoryFormData = z.infer<typeof createCategorySchema>
export type UpdateCategoryFormData = z.infer<typeof updateCategorySchema>

export const tagSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  color: z.string().optional(),
})

// Tag management form schema
export const createTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export const updateTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export type CreateTagFormData = z.infer<typeof createTagSchema>
export type UpdateTagFormData = z.infer<typeof updateTagSchema>

// Page management form schema
export const createPageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']),
  published_at: z.string().optional().nullable(),
  active: z.boolean(),
})

export const updatePageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']),
  published_at: z.string().optional().nullable(),
  active: z.boolean(),
})

export type CreatePageFormData = z.infer<typeof createPageSchema>
export type UpdatePageFormData = z.infer<typeof updatePageSchema>

export const navigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  path: z.string().min(1, 'Path is required'),
  icon: z.string().optional(),
  parent_id: z.number().optional(),
  permissions: z.array(z.string()).optional(),
})

// Navigation management form schema
export const basedNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean(),
  show_in_menu: z.boolean(),
})

export type basedNavigationFormData = z.infer<typeof basedNavigationSchema>

// Role management form schema
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required').max(255, 'Role name must not exceed 255 characters'),
  active: z.boolean(),
  permissions: z.record(
    z.string(), // parent navigation id
    z.record(
      z.string(), // navigation id
      z.record(
        z.string(), // permission name (can_view, can_create, etc.)
        z.boolean()
      )
    )
  ).refine(
    (permissions) => {
      // Check if at least one permission is set to true
      for (const parentNav in permissions) {
        for (const navId in permissions[parentNav]) {
          for (const permName in permissions[parentNav][navId]) {
            if (permissions[parentNav][navId][permName] === true) {
              return true
            }
          }
        }
      }
      return false
    },
    { message: 'Please select at least one permission' }
  ),
})

export const updateRoleSchema = createRoleSchema

export type CreateRoleFormData = z.infer<typeof createRoleSchema>
export type UpdateRoleFormData = z.infer<typeof updateRoleSchema>

export type LoginFormData = {
  email: string;
  password: string;
  remember_me?: boolean;
}
export type UserFormData = z.infer<typeof userSchema>
export type CategoryFormData = z.infer<typeof categorySchema>
export type TagFormData = z.infer<typeof tagSchema>
export type NavigationFormData = z.infer<typeof navigationSchema>

